/**
 * Settings → API & connector: self-serve Personal Access Tokens, the MCP connector and the API reference.
 *
 * PATs authenticate the public Video API (/api/v1/videos). The token value is
 * shown exactly once on creation. The API reference renders the OpenAPI spec
 * served at /api/v1/openapi.yaml.
 */

import { useEffect, useRef, useState } from 'react';
import { Copy, Loader2, Plus, Trash2, KeyRound, BookOpen, Check, Plug } from 'lucide-react';
import api, { getApiBaseUrl, getApiServerUrl } from '../../services/api';
import { connectorUrl } from '../../config/connector';
import { useFeature } from '../../hooks/useFeature';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

type ApiError = { response?: { data?: { error?: string; message?: string } } };
type RedocWindow = Window & { Redoc?: { init: (spec: string, options: object, el: HTMLElement) => void } };

interface Pat {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  isExpired: boolean;
  isRevoked: boolean;
}

type Tab = 'tokens' | 'connector' | 'reference';

const scopeLabels = (scopes: string[]): string[] => {
  if (scopes?.includes('sandbox')) return ['Test key'];
  return scopes?.length ? scopes : ['default'];
};

export function ApiSettings() {
  const [tab, setTab] = useState<Tab>('tokens');

  return (
    <div className="flex flex-col rounded-xl border bg-card text-card-foreground">
      <header className="shrink-0 border-b px-5 pt-5">
        <p className="text-muted-foreground text-sm">
          Create API tokens and drive video creation programmatically with the Video API.{' '}
          <a href="/developers/quickstart" className="text-primary underline underline-offset-2 hover:opacity-80">
            Read the quickstart guide
          </a>
          .
        </p>
        <div className="flex gap-1 mt-4 overflow-x-auto">
          <TabButton active={tab === 'tokens'} onClick={() => setTab('tokens')} icon={<KeyRound className="h-4 w-4" />}>
            API Tokens
          </TabButton>
          <TabButton active={tab === 'connector'} onClick={() => setTab('connector')} icon={<Plug className="h-4 w-4" />}>
            MCP Connector
          </TabButton>
          <TabButton active={tab === 'reference'} onClick={() => setTab('reference')} icon={<BookOpen className="h-4 w-4" />}>
            API Reference
          </TabButton>
        </div>
      </header>

      {tab === 'tokens' ? (
        <TokensPanel />
      ) : tab === 'connector' ? (
        <ConnectorPanel onGetToken={() => setTab('tokens')} />
      ) : (
        <div className="h-[75vh] min-h-[480px]">
          <ApiReference />
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function TokensPanel() {
  const [tokens, setTokens] = useState<Pat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const canMintTestKey = useFeature('api-sandbox');
  const [testKey, setTestKey] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/pats');
      setTokens(r.data.tokens || []);
      setError(null);
    } catch (err) {
      setError((err as ApiError)?.response?.data?.error || 'Could not load tokens. Are you signed in?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    setNewToken(null);
    try {
      const body: Record<string, unknown> = {};
      if (name.trim()) body.name = name.trim();
      const days = parseInt(expiresInDays, 10);
      if (!Number.isNaN(days) && days > 0) body.expiresInDays = days;
      if (testKey) body.sandbox = true;
      const r = await api.post('/pats', body);
      setNewToken(r.data.token);
      setName('');
      setExpiresInDays('');
      setTestKey(false);
      await load();
    } catch (err) {
      const data = (err as ApiError)?.response?.data;
      setError(data?.message || data?.error || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this token? Any client using it will immediately lose access.')) return;
    try {
      await api.delete(`/pats/${id}`);
      await load();
    } catch (err) {
      setError((err as ApiError)?.response?.data?.error || 'Failed to revoke token');
    }
  };

  const copyToken = () => {
    if (!newToken) return;
    navigator.clipboard?.writeText(newToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const active = tokens.filter((t) => !t.isRevoked && !t.isExpired);

  return (
    <div className="w-full px-5 py-6 space-y-6">
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Use a token as <code className="px-1 rounded bg-muted">Authorization: Bearer ivy_pat_…</code>.
        Treat it like a password — it carries your account's access.
      </p>

      {/* Newly created token (shown once) */}
      {newToken && (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-4 space-y-2">
          <p className="text-sm font-medium">Copy your token now — it won't be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-background border px-3 py-2 text-xs font-mono">{newToken}</code>
            <Button size="sm" variant="outline" onClick={copyToken}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Create form */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Production server" />
          </div>
          <div className="w-full sm:w-36">
            <label className="text-xs text-muted-foreground">Expires (days)</label>
            <Input value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} placeholder="never" inputMode="numeric" />
          </div>
          <Button onClick={create} disabled={creating} className="sm:w-auto">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Create token
          </Button>
        </div>
        {canMintTestKey && (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={testKey} onChange={(e) => setTestKey(e.target.checked)} />
            <span>
              <span className="font-medium">Test key</span>
              <span className="block text-xs text-muted-foreground">
                Calls get fake videos and spend no credits. A test key can't be turned into a live one, or a live one into a test key.
              </span>
            </span>
          </label>
        )}
      </div>

      {/* Token list */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground mb-2">Active tokens</h2>
        <div className="divide-y border rounded-lg">
          {loading ? (
            <div className="p-4 flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : active.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No active tokens yet. Create one above.</div>
          ) : (
            active.map((t) => (
              <div key={t.id} data-token-row className="flex items-start justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t.tokenPrefix}…··· · created {new Date(t.createdAt).toLocaleDateString()}
                    {t.expiresAt ? ` · expires ${new Date(t.expiresAt).toLocaleDateString()}` : ' · no expiry'}
                    {t.lastUsedAt ? ` · last used ${new Date(t.lastUsedAt).toLocaleDateString()}` : ' · never used'}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {scopeLabels(t.scopes).map((label) => (
                      <Badge key={label} data-token-scope variant="secondary">{label}</Badge>
                    ))}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="shrink-0" onClick={() => revoke(t.id)} aria-label="Revoke">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/** A labeled, copy-to-clipboard code block. */
function CopyBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-md border bg-muted/40">
      {label && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b text-xs text-muted-foreground">
          <span>{label}</span>
        </div>
      )}
      <div className="relative">
        <pre className="overflow-x-auto p-3 text-xs font-mono leading-relaxed">{code}</pre>
        <Button size="sm" variant="ghost" onClick={copy} className="absolute top-1.5 right-1.5 h-7 px-2" aria-label="Copy">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

/**
 * MCP Connector tab — connect Ivy to any agent (Claude Code, Desktop, claude.ai, Codex, ChatGPT, Cursor)
 * via the slim MCP server. Mirrors docs/connect-ivy-to-your-claude.md. The video
 * agents run server-side; only high-level tools cross to the user's client.
 */
function ConnectorPanel({ onGetToken }: { onGetToken: () => void }) {
  const mcpUrl = connectorUrl(getApiServerUrl());

  const cliCmd = `claude mcp add --transport http 4klabs ${mcpUrl} \\\n  --header "Authorization: Bearer ivy_pat_your_token"`;
  const codexCmd = `export IVY_PAT=ivy_pat_your_token\ncodex mcp add 4klabs --url ${mcpUrl} --bearer-token-env-var IVY_PAT`;
  // Cursor's one-click install: base64 of the server config; Cursor runs the OAuth sign-in itself.
  const cursorLink = `cursor://anysphere.cursor-deeplink/mcp/install?name=4klabs&config=${btoa(JSON.stringify({ url: mcpUrl }))}`;
  const desktopJson = JSON.stringify(
    { mcpServers: { '4klabs': { type: 'http', url: mcpUrl, headers: { Authorization: 'Bearer ivy_pat_your_token' } } } },
    null,
    2,
  );

  const tools: Array<[string, string]> = [
    ['get_model_credentials', 'Check whether your own model key (optional) is configured'],
    ['set_model_credentials', 'Optional: store your Anthropic key / Claude token / Bedrock + make it active'],
    ['create_video', 'Topic → new project; runs to the outline checkpoint'],
    ['poll', 'Resume a running generation/build turn'],
    ['get_outline', 'Scene breakdown + workspace (editable) + storyboard (shareable) URLs'],
    ['revise', 'Conversational edit at a checkpoint'],
    ['build', 'Approve the outline → produce the video'],
    ['get_status', 'Unified state (created → outline_ready → producing → delivering → delivered)'],
    ['deliver / get_delivery', 'A new player link (play.4klabs.ai) + MP4s for the video as it is now, and where they stand'],
    ['list_deliveries / add_delivery_mp4', "Every revision's own link and MP4s; more MP4s (e.g. 4K) of any revision"],
    ['publish', 'Public watch + embed URL'],
    ['export_video / get_export', 'Start an MP4 export and fetch the download URL'],
    ['list_videos', 'Your videos, most recent first'],
    ['get_credits / estimate_price / get_usage', 'Your balance, a price before you build, and what a video cost'],
    ['buy_credits', 'The credit packs on sale, and a checkout link for one'],
  ];

  return (
    <div className="max-w-3xl mx-auto px-5 py-6 space-y-8 text-sm">
      <section className="space-y-2">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Plug className="h-5 w-5" /> Connect 4K Labs to your Claude</h2>
        <p className="text-muted-foreground">
          Drive 4K Labs from your own Claude. Today that means creating narrated, audio-first videos —
          the work (script, narration, slides, assembly) runs on 4K Labs' servers; only a few high-level
          tools cross to your client. Usage is billed to your account's credits. The server is named{' '}
          <code className="px-1 rounded bg-muted">4klabs</code> in your client.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pt-1">
          <span><b className="text-foreground">Endpoint:</b> <code className="px-1 rounded bg-muted">{mcpUrl}</code></span>
          <span><b className="text-foreground">Transport:</b> Streamable HTTP</span>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">1. Get a token</h3>
        <p className="text-muted-foreground">
          Create a Personal Access Token on the{' '}
          <button onClick={onGetToken} className="text-primary underline underline-offset-2 hover:opacity-80">API Tokens tab</button>,
          then use it as the <code className="px-1 rounded bg-muted">Authorization</code> header below. claude.ai, ChatGPT and
          Cursor skip this step: they sign you in with your 4K Labs account instead.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">2. Add the connector</h3>
        <div className="space-y-1.5">
          <p className="text-muted-foreground text-xs font-medium">Claude Code (CLI)</p>
          <CopyBlock label="terminal" code={cliCmd} />
        </div>
        <div className="space-y-1.5">
          <p className="text-muted-foreground text-xs font-medium">Claude Desktop — claude_desktop_config.json</p>
          <CopyBlock label="json" code={desktopJson} />
        </div>
        <div className="space-y-1.5">
          <p className="text-muted-foreground text-xs font-medium">Codex (CLI) — Codex stores the variable name and sends the token when it connects</p>
          <CopyBlock label="terminal" code={codexCmd} />
        </div>
        <p className="text-muted-foreground text-xs">
          <b>claude.ai:</b> Settings → Connectors → Add custom connector → URL <code className="px-1 rounded bg-muted">{mcpUrl}</code>.
          Leave the OAuth client fields empty; you sign in with your 4K Labs account — no token needed.
        </p>
        <p className="text-muted-foreground text-xs">
          <b>ChatGPT:</b> Settings → Apps &amp; Connectors → Advanced → turn on Developer mode, then create a connector with URL{' '}
          <code className="px-1 rounded bg-muted">{mcpUrl}</code> and authentication <b>OAuth</b>; sign in with your 4K Labs account.
        </p>
        <p className="text-muted-foreground text-xs">
          <b>Cursor:</b>{' '}
          <a href={cursorLink} className="text-primary underline underline-offset-2 hover:opacity-80">Add to Cursor</a>{' '}
          — one click; Cursor opens the 4K Labs sign-in.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">3. Your own model key (optional)</h3>
        <p className="text-muted-foreground">
          You don't need one. Without a key, generation runs on 4K Labs' model accounts and your credits
          pay for it. With one, your own Anthropic account runs it (credits are still charged). To use
          your own, tell Claude to set it up — it calls{' '}
          <code className="px-1 rounded bg-muted">set_model_credentials</code> for you. Three options:
        </p>
        <ul className="text-muted-foreground list-disc pl-5 space-y-0.5">
          <li><b className="text-foreground">Anthropic API key</b> — from console.anthropic.com (<code className="px-1 rounded bg-muted">sk-ant-api…</code>)</li>
          <li><b className="text-foreground">Claude subscription token</b> — run <code className="px-1 rounded bg-muted">claude setup-token</code> locally (<code className="px-1 rounded bg-muted">sk-ant-oat…</code>)</li>
          <li><b className="text-foreground">AWS Bedrock</b> — a Bedrock bearer token + region</li>
        </ul>
        <blockquote className="border-l-2 border-primary/50 pl-3 italic text-muted-foreground">
          "Set my Anthropic API key: sk-ant-api…"
        </blockquote>
        <p className="text-muted-foreground text-xs">
          Your key is stored encrypted and used only to run your own generation. It's a secret, so it will
          appear in your Claude conversation — paste your own key. Ask Claude to{' '}
          <code className="px-1 rounded bg-muted">get_model_credentials</code> to check what's configured.
          (Skip this step to run on 4K Labs' accounts, or if you've already set a key in the app.)
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">4. Make a video</h3>
        <p className="text-muted-foreground">Just describe what you want; Claude drives the tools:</p>
        <blockquote className="border-l-2 border-primary/50 pl-3 italic text-muted-foreground">
          "Make me a 60-second explainer on the double-slit experiment for class 12, in a clear documentary voice."
        </blockquote>
        <p className="text-muted-foreground">
          It runs <code className="px-1 rounded bg-muted">create_video</code> → you review the outline in your
          workspace (Claude gives you a link) → <code className="px-1 rounded bg-muted">build</code> →{' '}
          <code className="px-1 rounded bg-muted">publish</code>. Generation takes a few minutes; long steps
          resume automatically via <code className="px-1 rounded bg-muted">poll</code>.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">Tools</h3>
        <div className="divide-y border rounded-lg">
          {tools.map(([name, desc]) => (
            <div key={name} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 px-3 py-2">
              <code className="text-xs font-mono text-primary shrink-0 sm:w-48">{name}</code>
              <span className="text-xs text-muted-foreground">{desc}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-1.5">
        <h3 className="font-semibold">Billing</h3>
        <p className="text-muted-foreground">
          1 credit per standard video, 4 per interactive — charged once when the video is assembled (at{' '}
          <code className="px-1 rounded bg-muted">build</code>), not at create and not on each publish. Out of
          credits → <code className="px-1 rounded bg-muted">create_video</code> returns a clear paywall error.
        </p>
      </section>
    </div>
  );
}

/**
 * Renders the OpenAPI spec with Redoc (loaded from CDN), filling the available
 * full-width / full-height area. Falls back to a raw spec link + curl quickstart
 * if the script can't load.
 */
function ApiReference() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const specUrl = `${getApiBaseUrl()}/v1/openapi.yaml`;

  useEffect(() => {
    let cancelled = false;
    const SCRIPT_SRC = 'https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js';

    const render = () => {
      const Redoc = (window as RedocWindow).Redoc;
      if (!Redoc || !containerRef.current || cancelled) return;
      try {
        Redoc.init(
          specUrl,
          {
            expandResponses: '200',
            nativeScrollbars: true,
            hideDownloadButton: false,
            theme: {
              colors: { primary: { main: '#7c3aed' } },
              rightPanel: { backgroundColor: '#0f1117', width: '34%' },
              typography: { fontSize: '15px' },
            },
          },
          containerRef.current,
        );
      } catch {
        setFailed(true);
      }
    };

    if ((window as RedocWindow).Redoc) { render(); return; }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', render);
    script.addEventListener('error', () => setFailed(true));
    return () => { cancelled = true; script.removeEventListener('load', render); };
  }, [specUrl]);

  if (failed) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-6 space-y-3 text-sm">
        <p className="text-muted-foreground">
          Interactive docs couldn't load. View the raw spec:{' '}
          <a href={specUrl} className="underline" target="_blank" rel="noreferrer">openapi.yaml</a>
        </p>
        <pre className="rounded bg-muted p-3 overflow-x-auto text-xs">
{`# Create a token (API Tokens tab), then:
curl -X POST ${getApiBaseUrl()}/v1/videos \\
  -H "Authorization: Bearer $IVY_PAT" \\
  -F "instructions=Make a 60s explainer about X" \\
  -F "qualityTier=basic" \\
  -F "files=@narration.mp3;type=audio/mpeg"
# qualityTier: basic (Sonnet 5.5, default) | pro (Opus 5.5). UltraMax is coming soon.`}
        </pre>
      </div>
    );
  }

  // Full-width, full-height container; Redoc manages its own internal scroll.
  return <div ref={containerRef} className="h-full w-full overflow-y-auto" />;
}
